/** @jsxImportSource @emotion/react */
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { IconButton, Skeleton, useMediaQuery } from '@mui/material'
import { useTheme } from '@emotion/react'
import { useMemo, useState } from 'react'
import { FIND_MY_USER, GET_PLATFORMS } from 'queries/common'
import { findMyUser } from '__generatedTypes__/findMyUser'
import { findAllPlatforms, findAllPlatformsVariables } from '__generatedTypes__/findAllPlatforms'
import { AI_TRAINER, authService, MARKETING_GROUP } from 'utils/services/auth'
import { useAuthContext } from 'utils/auth'
import { PlatformSlug, TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { ReactComponent as ArrowRightIcon } from './Menu/icons/arrow-right.svg'
import * as classes from './styles'
import PlatformSelector from './PlatformSelector'
import Menu from './Menu'
import User from './User'

const SKELETON_MENU = [
  {
    width: 70,
    items: [60, 80, 40, 110],
  },
  {
    width: 110,
    items: [100, 80, 90, 70],
  },
  {
    width: 90,
    items: [70, 50],
  },
]

export const Sidebar = () => {
  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { platform = '' } = useParams()
  const { groups } = useAuthContext()

  const { data: userData, loading: isLoadingUser } = useQuery<findMyUser>(FIND_MY_USER)

  const { data: platformsData, loading: isLoadingPlatforms } = useQuery<findAllPlatforms, findAllPlatformsVariables>(
    GET_PLATFORMS,
    {
      variables: { query: { take: TAKE_ALL_ITEMS_COUNT } },
    }
  )

  const handleSignOut = async () => {
    await authService.signOut()
  }

  const user = userData?.findMyUser
  const platforms = useMemo(() => {
    const allPlatforms = platformsData?.findAllPlatforms.items.map((platform) => ({
      ...platform,
      key: platform.slug,
    }))

    if (groups.includes(AI_TRAINER)) {
      return allPlatforms?.filter((platform) => platform.key === PlatformSlug.CasperBooster)
    }

    if (groups.includes(MARKETING_GROUP)) {
      return allPlatforms?.filter((platform) => platform.key !== PlatformSlug.Admissions)
    }

    return allPlatforms
  }, [platformsData, groups])

  if (isLoadingUser || isLoadingPlatforms) {
    return (
      <div css={[classes.wrapper, classes.skeleton]}>
        <Skeleton variant="rounded" height={64} />
        {SKELETON_MENU.map(({ width, items }, index) => (
          <div key={index}>
            <Skeleton variant="text" width={width} />
            <div>
              {items.map((width, index) => (
                <div key={index}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={width} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const getContent = () => (
    <div css={classes.wrapper}>
      <PlatformSelector
        selectedPlatformKey={platform}
        platforms={platforms ?? []}
        onCloseMenu={() => setIsMenuOpen(false)}
      />
      <Menu selectedPlatformKey={platform} onCloseMenu={() => setIsMenuOpen(false)} />
      <User user={user} onSignOut={handleSignOut} />
      <div css={classes.copyright}>
        &copy; {new Date().getFullYear()} Booster Prep Inc.
        <br />
        All Rights Reserved
      </div>
    </div>
  )

  if (isTabletOrMobile) {
    return (
      <>
        <div css={classes.clickAway(isMenuOpen)} onClick={() => setIsMenuOpen(false)} />
        <div css={classes.content(isMenuOpen)}>{getContent()}</div>
        <IconButton onClick={() => setIsMenuOpen((prev) => !prev)} css={classes.toggleButton(isMenuOpen)}>
          <ArrowRightIcon />
        </IconButton>
      </>
    )
  }

  return getContent()
}
